import React, { useRef, useEffect, useState, useCallback } from 'react';
import { MdArrowBack, MdArrowForward } from 'react-icons/md';
import * as XLSX from 'xlsx';
import { db, auth } from '../firebase';
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  doc,
  getDoc,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import '../styles/Modal.css';
import '../styles/Loading.css';
import { buildCampaignPropertySubject } from '../utils/campaignSubject';
import { normalizeUploadedCampaignScript } from '../utils/campaignComposer';
import { prepareActionAuditOutboxBatch } from '../utils/actionAudit';
import { buildExactCampaignLaunchOutbox } from '../utils/campaignLaunchOutbox';
import { deriveNonRequestableFields } from '../utils/nonRequestableFields';
import { validateCampaignLaunchDraft } from '../utils/draftGuards';
import { buildUploadedPropertyFromRow } from '../utils/uploadedPropertyRows';
import {
  describeUnemailedTabs,
  findNonViableDividerIndex,
  readRowHyperlinks,
  removeCarriedOverAutomationTabs,
} from '../utils/uploadedWorkbookShape';
import { deriveAskFields, deriveCampaignProperties } from '../utils/campaignAskFields';
import { verifyCampaignCreationAccess } from '../utils/campaignLaunchGuard';
import { applyEmailEditsToWorkbook } from '../utils/campaignWorkbook';
import { useCampaignAccess } from '../contexts/CampaignAccessContext';
import { useMailboxConnection } from '../contexts/NotificationsContext';
import { mailboxConnectedForLaunch } from '../utils/mailboxConnection';
import CampaignMaintenanceDialog from './CampaignMaintenanceDialog';
import ColumnMappingStep from './ColumnMappingStep';
import SheetAccessAccounts from './SheetAccessAccounts';
import StartProjectModal from './StartProjectModal';

// Initialize Firebase Functions
const functions = getFunctions();

// Keyword mapping for script-aware column defaults
const COLUMN_KEYWORDS = {
  total_sf: ['sf', 'square', 'footage', 'size'],
  rent_sf_yr: ['rent', 'asking'],
  ops_ex_sf: ['nnn', 'cam', 'ops', 'expense', 'operating'],
  ceiling_ht: ['height', 'clear', 'ceiling'],
  drive_ins: ['door', 'drive', 'drive-in'],
  docks: ['dock', 'loading'],
  power: ['power', 'electric', 'amp', 'volt'],
  flyer_link: ['flyer', 'brochure'],
  floorplan: ['floor', 'plan', 'layout'],
};

const FALLBACK_HEADER_ALIASES = {
  address: 'property_address',
  street_address: 'property_address',
  email_address: 'email',
  contact_email: 'email',
  e_mail: 'email',
  e_mail_address: 'email',
  broker_email: 'email',
  leasing_email: 'email',
  contact: 'leasing_contact',
  contact_name: 'leasing_contact',
  listing_broker: 'leasing_contact',
  listing_broker_contact: 'leasing_contact',
  broker_contact: 'leasing_contact',
  leasing_company_contact: 'leasing_contact',
  leasing_agent: 'leasing_contact',
  leasing_company_name: 'leasing_company',
};

const XLSX_ZIP_SIGNATURES = [
  [0x50, 0x4b, 0x03, 0x04],
  [0x50, 0x4b, 0x05, 0x06],
  [0x50, 0x4b, 0x07, 0x08],
];
const XLS_COMPOUND_FILE_SIGNATURE = [0xd0, 0xcf, 0x11, 0xe0, 0xa1, 0xb1, 0x1a, 0xe1];
const XLS_BIFF_SIGNATURES = [
  [0x09, 0x08],
  [0x09, 0x04],
  [0x09, 0x02],
  [0x09, 0x00],
];
const XLSX_WORKBOOK_CONTENT_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml';
const XLSM_WORKBOOK_CONTENT_TYPE = 'application/vnd.ms-excel.sheet.macroEnabled.main+xml';

function startsWithBytes(bytes, signature) {
  return bytes.length >= signature.length
    && signature.every((value, index) => bytes[index] === value);
}

function bytesToText(bytes) {
  // Package XML is UTF-8. Decode its bytes (including an optional BOM) instead
  // of turning UTF-8 bytes into unrelated single-byte characters. Invalid
  // encoding throws and is rejected by the existing upload error boundary.
  return decodeURIComponent(Array.from(bytes, byte => `%${byte.toString(16).padStart(2, '0')}`).join(''))
    .replace(/^\uFEFF/, '');
}

function parsePackageXml(xml) {
  const document = new DOMParser().parseFromString(xml, 'application/xml');
  if (document.getElementsByTagName('parsererror').length > 0) return null;
  return document;
}

function isMacroContentType(contentType) {
  const normalized = String(contentType || '').toLowerCase();
  return normalized.includes('macroenabled') || normalized.includes('vbaproject');
}

function inspectContentTypes(contentTypesXml, entries) {
  const document = parsePackageXml(contentTypesXml);
  if (!document) return { workbookContentType: '', hasMacroContentType: true };

  const overrides = Array.from(document.getElementsByTagNameNS('*', 'Override'));
  const defaults = Array.from(document.getElementsByTagNameNS('*', 'Default'));
  const workbookOverrides = overrides
    .filter(node => String(node.getAttribute('PartName') || '').toLowerCase() === '/xl/workbook.xml');
  const workbookDefaults = defaults
    .filter(node => String(node.getAttribute('Extension') || '').toLowerCase() === 'xml');
  // OPC resolves an exact part Override before the extension Default. A
  // duplicate applicable declaration is ambiguous and must not be accepted.
  const workbookDeclaration = workbookOverrides.length <= 1 && workbookDefaults.length <= 1
    ? workbookOverrides[0] || workbookDefaults[0]
    : null;
  const workbookType = workbookDeclaration?.getAttribute('ContentType') || '';
  const macroDefaultAppliesToEntry = defaults.some(node => {
    const contentType = node.getAttribute('ContentType') || '';
    const extension = String(node.getAttribute('Extension') || '').toLowerCase();
    if (!isMacroContentType(contentType)) return false;
    if (String(contentType).toLowerCase().includes('vbaproject')) return true;
    return Boolean(extension) && entries.some(entry => entry.path.endsWith(`.${extension}`));
  });

  return {
    workbookContentType: workbookType,
    hasMacroContentType: overrides.some(node => isMacroContentType(node.getAttribute('ContentType')))
      || macroDefaultAppliesToEntry,
  };
}

function packageEntries(container) {
  const paths = Array.isArray(container?.FullPaths) ? container.FullPaths : [];
  const files = Array.isArray(container?.FileIndex) ? container.FileIndex : [];
  return paths.map((path, index) => ({
    path: `/${String(path || '')
      .replace(/^Root Entry\/?/i, '')
      .replace(/^\/+/, '')
      .replace(/\\/g, '/')}`.toLowerCase(),
    content: files[index]?.content || null,
  }));
}

function hasMacroRelationship(entries) {
  return entries
    .filter(entry => entry.path.endsWith('.rels') && entry.content)
    .some(entry => {
      const document = parsePackageXml(bytesToText(entry.content));
      if (!document) return false;
      return Array.from(document.getElementsByTagNameNS('*', 'Relationship'))
        .some(node => {
          const type = String(node.getAttribute('Type') || '').toLowerCase();
          return type.includes('vbaproject') || type.includes('macro');
        });
    });
}

function hasLegacyVbaProject(entries) {
  return entries.some(entry => (
    /(?:^|\/)_(?:vba_project|vba_project_cur)(?:\/|$)/.test(entry.path)
      || /(?:^|\/)vba\/(?:dir|_vba_project)(?:\/|$)/.test(entry.path)
      || /(?:^|\/)macros\/(?:project|vba)(?:\/|$)/.test(entry.path)
  ));
}

function hasMatchingWorkbookSignature(fileName, bytes) {
  if (/\.xlsx$/i.test(fileName || '')) {
    if (!XLSX_ZIP_SIGNATURES.some(signature => startsWithBytes(bytes, signature))) {
      return false;
    }

    const container = XLSX.CFB.read(bytes, { type: 'array' });
    const contentTypes = XLSX.CFB.find(container, '/[Content_Types].xml');
    const workbook = XLSX.CFB.find(container, '/xl/workbook.xml');
    if (!contentTypes?.content || !workbook?.content) return false;
    const entries = packageEntries(container);
    const contentTypeInspection = inspectContentTypes(bytesToText(contentTypes.content), entries);
    const hasVbaProject = entries.some(entry => entry.path === '/xl/vbaproject.bin');
    if (
      contentTypeInspection.workbookContentType === XLSM_WORKBOOK_CONTENT_TYPE
      || contentTypeInspection.hasMacroContentType
      || hasVbaProject
      || hasMacroRelationship(entries)
    ) {
      return false;
    }
    return contentTypeInspection.workbookContentType === XLSX_WORKBOOK_CONTENT_TYPE;
  }

  if (/\.xls$/i.test(fileName || '')) {
    if (startsWithBytes(bytes, XLS_COMPOUND_FILE_SIGNATURE)) {
      const container = XLSX.CFB.read(bytes, { type: 'array' });
      return !hasLegacyVbaProject(packageEntries(container));
    }
    return XLS_BIFF_SIGNATURES.some(signature => startsWithBytes(bytes, signature));
  }

  return false;
}

/**
 * Analyze script text and return canonical keys of columns mentioned.
 */
function analyzeScriptFields(scripts) {
  if (!scripts || scripts.length === 0) return [];
  const combined = scripts.join(' ').toLowerCase();
  const matched = [];
  for (const [canonicalKey, keywords] of Object.entries(COLUMN_KEYWORDS)) {
    if (keywords.some(kw => combined.includes(kw))) {
      matched.push(canonicalKey);
    }
  }
  return matched;
}

function deriveLeaseHeaders(leaseSheet, leaseData) {
  const headers = [];
  const seen = new Set();
  const addHeader = (value) => {
    const header = String(value ?? '');
    const identity = header.trim().toLowerCase();
    if (!identity || seen.has(identity)) return;
    seen.add(identity);
    headers.push(header);
  };

  if (leaseSheet?.['!ref']) {
    const range = XLSX.utils.decode_range(leaseSheet['!ref']);
    if (range.s.r <= 1 && range.e.r >= 1) {
      for (let column = range.s.c; column <= range.e.c; column += 1) {
        const address = XLSX.utils.encode_cell({ r: 1, c: column });
        addHeader(leaseSheet[address]?.v);
      }
    }
  }

  (leaseData || []).forEach(row => {
    Object.keys(row || {}).forEach(addHeader);
  });

  return headers;
}

// The identity columns (and the flyer column) of the column mapping, in the
// shape ColumnMappingStep's handleConfirm returns them. Identity columns are
// not editable in that step, so this is the mapping the advisor confirms.
function uploadReadMappingsFromColumns(columns = []) {
  const mappings = {};
  columns.forEach(col => {
    if (!col?.canonicalKey || !col.header) return;
    if (col.category !== 'identification' && col.canonicalKey !== 'flyer_link') return;
    if (!mappings[col.canonicalKey]) mappings[col.canonicalKey] = col.header;
  });
  return mappings;
}

// Group the uploaded rows by recipient. `readOptions` carries the confirmed
// column mapping once it is known, so the rows the advisor reviews are read
// through the same columns the Sheet and the send check use.
function collectUploadedProperties(leaseData, readOptions = {}, rowLinks = []) {
  const emailToContactMap = {};
  const emailToProperties = {};
  const allProperties = [];
  const validEmails = [];
  const uniqueEmails = new Set();

  // Rows at or below a NON-VIABLE divider (a re-uploaded SiteSift Sheet) were
  // ruled out in the earlier campaign: they are never read as properties.
  const stopAt = Number.isInteger(readOptions.dividerIndex) && readOptions.dividerIndex >= 0
    ? readOptions.dividerIndex
    : Infinity;

  (leaseData || []).forEach((row, index) => {
    if (index >= stopAt) return;
    const property = buildUploadedPropertyFromRow(row, index, {
      ...readOptions,
      links: rowLinks[index] || null,
    });
    const email = property.email || '';
    const contact = property.contact || '';
    const hasValidEmail = typeof email === 'string' && email.includes('@');

    // Only include rows that have at least an address
    if (property.address) {
      allProperties.push(property);
    }

    if (hasValidEmail) {
      const emailLower = email.toLowerCase();
      validEmails.push(email);
      uniqueEmails.add(emailLower);

      if (typeof contact === 'string' && contact.length > 0) {
        emailToContactMap[email] = contact;
      }

      if (!emailToProperties[email]) {
        emailToProperties[email] = [];
      }
      emailToProperties[email].push(property);
    }
  });

  return { emailToContactMap, emailToProperties, allProperties, validEmails, uniqueEmails };
}

// Fun loading phrases for Excel upload
const UPLOAD_LOADING_PHRASES = [
  "Reading your spreadsheet...",
  "Counting properties...",
  "Finding broker contacts...",
  "Analyzing column headers...",
  "Detecting email addresses...",
  "Parsing property data...",
  "Mapping data fields...",
  "Checking for duplicates...",
  "Validating email formats...",
  "Extracting property details...",
  "Processing Excel formulas...",
  "Identifying lease data...",
  "Scanning for scripts...",
  "Building contact list...",
  "Organizing broker info...",
  "Reading property addresses...",
  "Parsing city names...",
  "Detecting column types...",
  "Analyzing data patterns...",
  "Preparing column mapping...",
  "Looking for hidden gems...",
  "Crunching the numbers...",
  "Making sense of it all...",
  "Almost there...",
  "Just a bit more...",
  "Wrapping things up...",
  "Double-checking everything...",
  "Connecting the dots...",
  "Putting it all together...",
  "Finalizing analysis...",
];

// Fun loading phrases for sheet creation
const SHEET_CREATION_PHRASES = [
  "Creating your Google Sheet...",
  "Setting up columns...",
  "Formatting headers...",
  "Importing property data...",
  "Configuring formulas...",
  "Setting up data validation...",
  "Organizing rows...",
  "Applying styles...",
  "Building the tracker...",
  "Preparing for automation...",
  "Connecting to Google Drive...",
  "Uploading property info...",
  "Finalizing sheet structure...",
  "Adding broker contacts...",
  "Setting up extraction fields...",
  "Configuring column widths...",
  "Almost ready...",
  "Polishing things up...",
  "Double-checking formulas...",
  "Verifying data integrity...",
  "Setting permissions...",
  "Preparing email queue...",
  "Getting everything lined up...",
  "Just a few more seconds...",
  "Making it look nice...",
  "Crossing the t's...",
  "Dotting the i's...",
  "Final touches...",
  "Wrapping up nicely...",
  "Nearly there...",
];

export default function AddClientModal({ onClose, onSave, existingNames }) {
  const { refresh: refreshCampaignAccess, maintenanceMessage } = useCampaignAccess();
  // The same "is the mailbox connected?" answer the existing-campaign path uses,
  // read from the one listener the provider already runs: a first campaign must
  // be held for the same reason, in the same words, as every later one.
  const mailboxConnected = mailboxConnectedForLaunch(useMailboxConnection());
  const modalRef = useRef(null);
  const fileInputRef = useRef(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Creating Client...');
  const [isUploadPhase, setIsUploadPhase] = useState(false); // Track if we're in upload/analysis phase
  const [isCreatingPhase, setIsCreatingPhase] = useState(false); // Track if we're in sheet creation phase
  const phraseIndexRef = useRef(0);
  const creatingPhraseIndexRef = useRef(0);
  const uploadOperationRef = useRef(0);
  const activeFileReaderRef = useRef(null);
  // The one column analysis this wizard has paid for, keyed by the exact
  // payload that was sent. See analyzeWorkbookColumns below.
  const columnAnalysisCacheRef = useRef(null);

  // Multi-step flow state
  const [step, setStep] = useState('upload'); // 'upload' | 'mapping' | 'campaign' | 'creating' | 'complete'
  // R14 1.7: the mapping step unmounts while the outreach step is shown, so its
  // page and column settings are held here and handed back on Back.
  const mappingDraftRef = useRef(null);
  const [parsedData, setParsedData] = useState(null); // Parsed Excel data
  const [columnAnalysis, setColumnAnalysis] = useState(null); // Analysis from AI
  const [columnConfig, setColumnConfig] = useState(null); // Confirmed column config
  const [scriptMentionedFields, setScriptMentionedFields] = useState([]);
  const [validationIssues, setValidationIssues] = useState([]); // Issues to show on Page 1
  const [createdClient, setCreatedClient] = useState(null); // Store created client for completion screen
  const [campaignMaintenance, setCampaignMaintenance] = useState(null);

  useEffect(() => {
    const fetchUserData = async () => {
      const docRef = doc(db, 'users', auth.currentUser.uid);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        setUserData(docSnap.data());
      }
    };
    fetchUserData();
  }, []);

  // Cycle through loading phrases during upload phase
  useEffect(() => {
    if (!isUploadPhase || !loading) {
      phraseIndexRef.current = 0;
      return;
    }

    // Set initial phrase
    setLoadingMessage(UPLOAD_LOADING_PHRASES[0]);

    const interval = setInterval(() => {
      phraseIndexRef.current = (phraseIndexRef.current + 1) % UPLOAD_LOADING_PHRASES.length;
      setLoadingMessage(UPLOAD_LOADING_PHRASES[phraseIndexRef.current]);
    }, 2000); // Change every 2 seconds

    return () => clearInterval(interval);
  }, [isUploadPhase, loading]);

  // Cycle through loading phrases during sheet creation phase
  useEffect(() => {
    if (!isCreatingPhase || !loading) {
      creatingPhraseIndexRef.current = 0;
      return;
    }

    // Set initial phrase
    setLoadingMessage(SHEET_CREATION_PHRASES[0]);

    const interval = setInterval(() => {
      creatingPhraseIndexRef.current = (creatingPhraseIndexRef.current + 1) % SHEET_CREATION_PHRASES.length;
      setLoadingMessage(SHEET_CREATION_PHRASES[creatingPhraseIndexRef.current]);
    }, 2000); // Change every 2 seconds

    return () => clearInterval(interval);
  }, [isCreatingPhase, loading]);

  const cancelActiveUpload = useCallback(() => {
    uploadOperationRef.current += 1;
    const reader = activeFileReaderRef.current;
    activeFileReaderRef.current = null;

    if (reader && reader.readyState !== 2 && typeof reader.abort === 'function') {
      try {
        reader.abort();
      } catch (error) {
        console.error('Failed to abort workbook reader:', error);
      }
    }

    setLoading(false);
    setIsUploadPhase(false);
  }, []);

  // One paid column analysis per distinct workbook.
  //
  // `analyzeSheetColumns` runs a model on the owner's key, so an identical
  // payload must never be charged twice. The in-flight promise is kept against
  // the payload that produced it: choosing the same workbook again — most
  // often by coming Back from the mapping step and picking the same file —
  // re-awaits that promise instead of paying for a second call, and two
  // selections that overlap collapse onto the one call already in flight.
  // A rejection is never kept, so a failed analysis can still be retried.
  const analyzeWorkbookColumns = useCallback((excelBase64) => {
    const cached = columnAnalysisCacheRef.current;
    if (cached && cached.key === excelBase64) {
      return cached.promise;
    }

    const analyzeSheetColumns = httpsCallable(functions, 'analyzeSheetColumns');
    const promise = analyzeSheetColumns({ excelBase64 });
    columnAnalysisCacheRef.current = { key: excelBase64, promise };
    promise.catch(() => {
      if (columnAnalysisCacheRef.current && columnAnalysisCacheRef.current.promise === promise) {
        columnAnalysisCacheRef.current = null;
      }
    });
    return promise;
  }, []);

  useEffect(() => () => {
    uploadOperationRef.current += 1;
    const reader = activeFileReaderRef.current;
    activeFileReaderRef.current = null;
    if (reader && reader.readyState !== 2 && typeof reader.abort === 'function') {
      try {
        reader.abort();
      } catch (error) {
        console.error('Failed to abort workbook reader during cleanup:', error);
      }
    }
  }, []);

  // Cancel with confirmation (for when user has started uploading)
  const handleCancel = useCallback(() => {
    if (step === 'upload') {
      cancelActiveUpload();
      onClose();
      return;
    }

    const confirmed = window.confirm(
      'Are you sure you want to cancel?\n\nThis will discard the file you uploaded and any column settings. You\'ll need to start over.'
    );
    if (confirmed) {
      cancelActiveUpload();
      onClose();
    }
  }, [step, onClose, cancelActiveUpload]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape' && !loading) {
        handleCancel();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [loading, handleCancel]);

  useEffect(() => {
    const onClick = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target) && !loading) {
        handleCancel();
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [loading, handleCancel]);

  // Step 1: Handle file upload and parse Excel
  const handleFileUpload = async (e) => {
    cancelActiveUpload();
    setParsedData(null);
    setColumnAnalysis(null);
    setScriptMentionedFields([]);

    const file = e.target.files[0];
    if (!file) return;

    const refuseFile = (message) => {
      setValidationIssues([{ type: 'error', field: 'file', message }]);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    };

    if (!/\.(xlsx|xls)$/i.test(file.name || '')) {
      refuseFile('Choose an Excel workbook (.xlsx or .xls).');
      return;
    }

    if (file.size === 0) {
      refuseFile('Choose a non-empty Excel workbook (.xlsx or .xls).');
      return;
    }

    const operationId = uploadOperationRef.current + 1;
    uploadOperationRef.current = operationId;
    setValidationIssues([]);

    const reader = new FileReader();
    activeFileReaderRef.current = reader;

    const isCurrentOperation = () => uploadOperationRef.current === operationId;
    const finishOperation = () => {
      if (!isCurrentOperation()) return;
      uploadOperationRef.current += 1;
      if (activeFileReaderRef.current === reader) {
        activeFileReaderRef.current = null;
      }
      setLoading(false);
      setIsUploadPhase(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    };

    const failReader = () => {
      if (!isCurrentOperation()) return;
      refuseFile('We could not read that Excel workbook. Choose a non-empty .xlsx or .xls file exported from CoStar.');
      finishOperation();
    };

    reader.onerror = failReader;
    reader.onabort = failReader;
    reader.onload = async (event) => {
      if (!isCurrentOperation()) return;
      setLoading(true);
      setIsUploadPhase(true); // Start cycling phrases

      try {
        const data = new Uint8Array(event.target.result);
        if (!isCurrentOperation()) return;
        if (!hasMatchingWorkbookSignature(file.name, data)) {
          refuseFile('The file contents do not match an Excel workbook. Choose a genuine .xlsx or .xls file exported from CoStar.');
          return;
        }
        // Preserve full Excel file as base64
        let base64Excel = btoa(
          Array.from(new Uint8Array(event.target.result))
            .map(byte => String.fromCharCode(byte))
            .join('')
        );
        const workbook = XLSX.read(data, { type: 'array' });
        const hasParsedMacroSheet = Object.values(workbook.Sheets || {}).some(sheet => (
          String(sheet?.['!type'] || '').trim().toLowerCase() === 'macro'
        ));
        if (hasParsedMacroSheet) {
          refuseFile('The file contents do not match an Excel workbook. Choose a genuine .xlsx or .xls file exported from CoStar.');
          return;
        }

        // A re-uploaded SiteSift Sheet: its AI_META tab never reaches the new
        // campaign Sheet (the Sheet is created from this base64).
        if (removeCarriedOverAutomationTabs(workbook).length > 0) {
          base64Excel = XLSX.write(workbook, { type: 'base64', bookType: 'xlsx' });
        }

        // Try "FOR LEASE" sheet first, fall back to first sheet
        let leaseSheet = workbook.Sheets['FOR LEASE'];
        let usedSheetName = 'FOR LEASE';
        if (!leaseSheet && workbook.SheetNames.length > 0) {
          usedSheetName = workbook.SheetNames[0];
          leaseSheet = workbook.Sheets[usedSheetName];
          console.log(`"FOR LEASE" sheet not found, using first sheet: "${usedSheetName}"`);
        }
        const scriptSheet = workbook.Sheets['Script'];

        // Try to get client name from A1
        let name = leaseSheet?.A1?.v?.trim() || '';
        if (name.startsWith('Client:')) {
          name = name.replace(/^Client:\s*/, '');
        }

        // If no name in A1, try to extract from filename
        if (!name && file.name) {
          // Remove extension and clean up filename
          let fileBaseName = file.name.replace(/\.(xlsx|xls|csv)$/i, '');
          // Remove common prefixes/suffixes
          fileBaseName = fileBaseName.replace(/^(Scrub|Data|Export|Properties)\s*[-_]?\s*/i, '');
          fileBaseName = fileBaseName.replace(/\s*[-_]?\s*(data|export|final|v\d+)$/i, '');
          if (fileBaseName.trim()) {
            name = fileBaseName.trim();
            console.log(`Extracted client name from filename: "${name}"`);
          }
        }

        // Extract ALL non-empty scripts from Script sheet column A
        const emailScripts = [];
        if (scriptSheet) {
          const range = XLSX.utils.decode_range(scriptSheet['!ref'] || 'A1:A1');
          for (let row = range.s.r; row <= range.e.r; row++) {
            const cellAddress = XLSX.utils.encode_cell({ r: row, c: 0 });
            const cell = scriptSheet[cellAddress];
            if (cell && cell.v && typeof cell.v === 'string' && cell.v.trim()) {
              emailScripts.push(cell.v.trim());
            }
          }
        }
        console.log(`Found ${emailScripts.length} email script(s) in Script sheet`);

        const leaseData = XLSX.utils.sheet_to_json(leaseSheet, { range: 1, blankrows: true });

        // Create mappings and capture ALL properties (even without emails).
        // This first read matches headers by their known spellings; once the
        // column analysis returns, the rows are read again through it.
        const leaseHeaders = deriveLeaseHeaders(leaseSheet, leaseData);
        const leaseRowLinks = readRowHyperlinks(leaseSheet, leaseData.length);
        const dividerIndex = findNonViableDividerIndex(leaseSheet, leaseData.length);
        const ruledOutCount = dividerIndex < 0 ? 0 : leaseData
          .slice(dividerIndex + 1)
          .filter((row, offset) => buildUploadedPropertyFromRow(row, dividerIndex + 1 + offset).address)
          .length;
        const {
          emailToContactMap,
          emailToProperties,
          allProperties,
          validEmails,
          uniqueEmails,
        } = collectUploadedProperties(leaseData, { headers: leaseHeaders, dividerIndex }, leaseRowLinks);

        console.log(`Found ${allProperties.length} properties, ${uniqueEmails.size} with valid emails`);

        // The Script sheet is a finished email: personalize its greeting and drop
        // the advisor's own signature here, so the stored campaign already
        // carries the message the outreach step and the send gate expect.
        const workbookContactNames = Object.values(emailToContactMap)
          .filter(name => typeof name === 'string' && name.trim());
        const normalizedScripts = emailScripts.map(
          script => normalizeUploadedCampaignScript(script, {
            contactNames: workbookContactNames,
          })
        );

        // Collect validation issues - warnings allow proceeding, errors block
        const issues = [];

        if (!name) {
          // Not an error - user can enter name in UI
          name = '';  // Leave empty so UI shows input field
        }

        if (ruledOutCount > 0) {
          issues.push({
            type: 'warning',
            field: 'file',
            message: `This is a downloaded SiteSift Sheet: the ${ruledOutCount} ${ruledOutCount === 1 ? 'property' : 'properties'} below its NON-VIABLE divider ${ruledOutCount === 1 ? 'was' : 'were'} ruled out in the earlier campaign and will not be emailed.`,
          });
        }

        describeUnemailedTabs(workbook, usedSheetName).forEach(tab => {
          issues.push({
            type: 'warning',
            field: 'file',
            message: `The "${tab.name}" tab (${tab.rowCount} ${tab.rowCount === 1 ? 'row' : 'rows'}) is copied into the campaign Sheet, but its rows will not be emailed. Only the "${usedSheetName}" tab is emailed.`,
          });
        });

        // Check for duplicate names (will be re-checked when user edits)
        if (name && existingNames.includes(name)) {
          issues.push({ type: 'warning', field: 'name', message: `A client named "${name}" already exists - you can edit it below` });
        }

        if (allProperties.length === 0) {
          setValidationIssues([{
            type: 'error',
            field: 'file',
            message: 'No properties were found. Use a CoStar workbook with headers in row 2 and property data starting in row 3.'
          }]);
          return;
        }

        setValidationIssues(issues);

        // Analyze scripts for column keyword matching
        const mentionedFields = analyzeScriptFields(normalizedScripts);
        setScriptMentionedFields(mentionedFields);

        // Store parsed data for later
        setParsedData({
          name,
          // The workbook-derived name is only a suggestion. Kept separately so
          // the naming step can tell it apart from a name the user has chosen.
          suggestedName: name,
          validEmails,
          emailToContactMap,
          emailToProperties,
          allProperties,
          emailScripts,
          normalizedScripts,
          base64Excel
        });

        // Call Firebase function to analyze columns
        try {
          const analysisResult = await analyzeWorkbookColumns(base64Excel);
          if (!isCurrentOperation()) return;

          const mappedRows = collectUploadedProperties(leaseData, {
            headers: leaseHeaders,
            dividerIndex,
            mappings: uploadReadMappingsFromColumns(analysisResult.data?.columns),
          }, leaseRowLinks);
          setParsedData(prev => (prev ? {
            ...prev,
            validEmails: mappedRows.validEmails,
            emailToContactMap: mappedRows.emailToContactMap,
            emailToProperties: mappedRows.emailToProperties,
            allProperties: mappedRows.allProperties,
          } : prev));
          setColumnAnalysis(analysisResult.data);
          setStep('mapping');
        } catch (analysisError) {
          if (!isCurrentOperation()) return;
          console.error('Column analysis failed, using default config:', analysisError);
          // If analysis fails, build a default columns array from detected headers
          const rawHeaders = leaseHeaders;
          const defaultColumns = rawHeaders.map((header, index) => {
            const normalizedKey = header.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
            const key = FALLBACK_HEADER_ALIASES[normalizedKey] || normalizedKey;
            const identificationKeys = ['property_address', 'city', 'property_name', 'leasing_company', 'leasing_contact', 'email'];
            const isIdentification = identificationKeys.includes(key);
            return {
              index,
              header,
              category: isIdentification ? 'identification' : 'extractable',
              defaultMode: isIdentification ? 'gather' : 'gather',
              isFormula: false,
              formulaInfo: null,
              canonicalKey: key,
              confidence: 0
            };
          });
          const mappedRows = collectUploadedProperties(leaseData, {
            headers: leaseHeaders,
            dividerIndex,
            mappings: uploadReadMappingsFromColumns(defaultColumns),
          }, leaseRowLinks);
          setParsedData(prev => (prev ? {
            ...prev,
            validEmails: mappedRows.validEmails,
            emailToContactMap: mappedRows.emailToContactMap,
            emailToProperties: mappedRows.emailToProperties,
            allProperties: mappedRows.allProperties,
          } : prev));
          setColumnAnalysis({
            columns: defaultColumns,
            hasEmailColumn: rawHeaders.some(h => h.toLowerCase().includes('email')),
            emailColumnIndex: rawHeaders.findIndex(h => h.toLowerCase().includes('email')),
            warnings: ['Column analysis failed - using defaults']
          });
          setStep('mapping');
        }
      } catch (err) {
        if (!isCurrentOperation()) return;
        console.error('Failed to process Excel file:', err);
        setValidationIssues([{
          type: 'error',
          field: 'file',
          message: 'We could not read that Excel workbook. Choose a non-empty .xlsx or .xls file exported from CoStar.'
        }]);
      } finally {
        finishOperation();
      }
    };

    try {
      reader.readAsArrayBuffer(file);
    } catch (error) {
      failReader();
    }
  };

  // Step 2: Handle column mapping confirmation -> advance to the sheet access step
  const handleMappingConfirm = (config) => {
    // Extract row modifications and client name from config
    const { editedEmails = {}, enabledRowIds = [], clientName, ...columnConfigOnly } = config;

    // Apply email edits and filter to enabled rows only
    if (parsedData) {
      const enabledSet = new Set(enabledRowIds);
      let reviewedWorkbook;

      // Values recovered from hyperlinks (a mailto on the broker's name, a URL
      // behind "Flyer") are written into the workbook the Sheet is built from,
      // which keeps cell text and drops links. An advisor's own edit wins.
      const linkedEmails = {};
      const linkedFlyers = {};
      parsedData.allProperties.forEach((prop, index) => {
        if (prop.emailFromLink && editedEmails[index] === undefined) linkedEmails[index] = prop.email;
        if (prop.flyerFromLink) linkedFlyers[index] = prop.flyerLink;
      });

      try {
        reviewedWorkbook = applyEmailEditsToWorkbook({
          base64Excel: parsedData.base64Excel,
          emailHeader: columnConfigOnly.mappings?.email,
          properties: parsedData.allProperties,
          editedEmails: { ...linkedEmails, ...editedEmails },
          flyerHeader: columnConfigOnly.mappings?.flyer_link,
          editedFlyers: linkedFlyers,
        });
      } catch (error) {
        console.error('Failed to align reviewed recipients with workbook:', error);
        alert(`Could not update the campaign Sheet with the reviewed recipients. ${error.message}`);
        return;
      }

      // Filter allProperties to only enabled rows and apply email edits
      const filteredProperties = parsedData.allProperties
        .filter((_, index) => enabledSet.has(index))
        .map((prop, originalIndex) => {
          // Find the original index in allProperties to look up edits
          const actualIndex = parsedData.allProperties.indexOf(prop);
          const editedEmail = editedEmails[actualIndex];
          return {
            ...prop,
            email: editedEmail !== undefined ? editedEmail : prop.email
          };
        });

      // Rebuild emailToProperties from filtered properties
      const newEmailToProperties = {};
      const newValidEmails = [];
      const newEmailToContactMap = {};

      filteredProperties.forEach(prop => {
        if (prop.email && prop.email.includes('@')) {
          if (!newEmailToProperties[prop.email]) {
            newEmailToProperties[prop.email] = [];
          }
          newEmailToProperties[prop.email].push(prop);
          if (!newValidEmails.includes(prop.email)) {
            newValidEmails.push(prop.email);
          }
          if (prop.contact && !newEmailToContactMap[prop.email]) {
            newEmailToContactMap[prop.email] = prop.contact;
          }
        }
      });

      // Update parsedData with filtered/modified properties and client name
      setParsedData(prev => ({
        ...prev,
        name: clientName || prev.name,  // Use edited client name
        // Whether edited or accepted unchanged, this value is now the user's
        // campaign name rather than a pending workbook suggestion. Clearing the
        // offer prevents the mapping step from selecting it again on return.
        suggestedName: '',
        allProperties: filteredProperties,
        emailToProperties: newEmailToProperties,
        validEmails: newValidEmails,
        emailToContactMap: { ...prev.emailToContactMap, ...newEmailToContactMap },
        base64Excel: reviewedWorkbook,
      }));
    }

    setColumnConfig(columnConfigOnly);
    setStep('sheetAccess');
  };

  // Step 4: Handle campaign confirm -> create client + sheet + outbox all at once
  const handleCampaignConfirm = async (campaignData) => {
    if (!parsedData || !userData || !columnConfig) return;

    const access = await verifyCampaignCreationAccess(refreshCampaignAccess);
    if (!access.allowed) {
      setCampaignMaintenance(access.message || maintenanceMessage);
      return;
    }

    setLoading(true);
    setIsCreatingPhase(true); // Start cycling phrases
    setStep('creating');

    let createdDocRef = null;
    try {
      const user = auth.currentUser;
      if (!user) throw new Error("User not authenticated");

      const { name, validEmails, emailToContactMap, emailToProperties, emailScripts, normalizedScripts, base64Excel } = parsedData;
      const requestedPersonalizedEmails = Array.isArray(campaignData.personalizedEmails)
        ? campaignData.personalizedEmails
        : [];
      const personalizedEmails = requestedPersonalizedEmails
        .filter(email => email?.email && email?.script);
      if (personalizedEmails.length === 0) {
        throw new Error('No safe campaign emails were generated. Review the recipients and campaign draft before trying again.');
      }
      const omittedEmailCount = requestedPersonalizedEmails.length - personalizedEmails.length;
      if (omittedEmailCount > 0) {
        throw new Error(
          `${omittedEmailCount} personalized email${omittedEmailCount === 1 ? '' : 's'} would be skipped because a recipient or script is missing. Review the campaign draft before trying again.`
        );
      }
      const askFields = deriveAskFields(columnConfig);
      // The other half of the column contract, plus one id tying this launch's
      // items together, so the server can refuse the launch as a whole.
      const nonRequestableFields = deriveNonRequestableFields(columnConfig);

      // Step 1: create Firestore doc with columnConfig
      const clientData = {
        name,
        assignedEmails: validEmails,
        emailToProperties: campaignData.emailToProperties || emailToProperties,
        emailScripts: campaignData.emailScripts || normalizedScripts,
        originalScripts: emailScripts,
        criteria: (campaignData.emailScripts || normalizedScripts)[0] || '',
        secondaryCriteria: (campaignData.emailScripts || normalizedScripts)[1] || null,
        emailToContactMap: emailToContactMap,
        columnConfig: columnConfig,
        extractionFields: columnConfig.extractionFields || [],
        createdAt: serverTimestamp(),
        lastUpdated: serverTimestamp(),
        status: "new"
      };

      // Add followUpConfig if provided
      if (campaignData.followUpConfig) {
        clientData.followUpConfig = campaignData.followUpConfig;
        clientData.followUpCount = campaignData.followUpConfig.followUps?.length || 0;
        console.log('🔧 [AddClientModal] Saving followUpConfig:', JSON.stringify(campaignData.followUpConfig, null, 2));
      }

      // Persist the campaign's send mode (separate vs combined-per-broker)
      if (campaignData.sendMode) {
        clientData.sendMode = campaignData.sendMode;
      }

      createdDocRef = await addDoc(collection(db, 'users', user.uid, 'clients'), clientData);

      const clientId = createdDocRef.id;

      // Step 2: create Google Sheet
      //
      // The endpoint verifies this token and derives the owner and the share
      // address from it, so a campaign can only ever be created for the person
      // signed in here. An older deployment simply ignores the extra header,
      // which is what lets this ship before the function does.
      const idToken = await auth.currentUser.getIdToken();
      const res = await fetch("https://api-cca6hhnsxa-uc.a.run.app", {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`
        },
        body: JSON.stringify({
          uid: user.uid,
          clientId,
          clientName: name,
          shareWithEmail: userData.email,
          excelBase64: base64Excel,
          columnConfig
        })
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        const errorMsg = errorData.details || errorData.error || `HTTP ${res.status}`;
        throw new Error(`Failed to create Google Sheet: ${errorMsg}`);
      }

      const { url: sheetUrl, sheetId } = await res.json();

      // Step 3: update Firestore with sheet URL
      await updateDoc(createdDocRef, {
        excelUrl: sheetUrl,
        sheetId: sheetId
      });

      // Step 3b: share the new sheet with the advisor's other Google accounts.
      //
      // This runs only once the id is ON the campaign document, because the
      // callable will not touch a sheet it cannot find among the caller's own
      // campaigns. It is best effort by design: the sheet and the campaign
      // exist either way, and Settings can repair the access later.
      try {
        const syncSheetAccess = httpsCallable(functions, 'updateSheetShareEmails');
        await syncSheetAccess({ action: 'sync', sheetId });
      } catch (sheetAccessError) {
        console.warn(
          'Campaign created; sharing its sheet with your other Google accounts did not finish:',
          sheetAccessError?.message || sheetAccessError
        );
      }

      // Step 4: atomically publish the campaign with its audited outbox work.
      // A fresh id per attempt: a refused launch must never block a relaunch.
      const launchId = `${clientId}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      const campaignBatch = prepareActionAuditOutboxBatch({
        uid: user.uid,
        clientUpdate: {
          clientId,
          data: { status: 'live' }
        },
        entries: personalizedEmails.map(emailData => {
            const validation = validateCampaignLaunchDraft(emailData.script, {
              askFields,
              nonRequestableFields,
            });
            if (!validation.ok) {
              throw new Error(`Campaign email for ${emailData.email} is not safe to queue. ${validation.errors.join(' ')}`);
            }
            const property = emailData.property || {};
            const subject = buildCampaignPropertySubject(property);

            return {
              audit: {
                actionType: 'campaign_creation',
                client: { id: clientId, name },
                subject: subject || '',
                finalBody: emailData.script,
                finalRecipients: [emailData.email],
                source: 'dashboard_new_campaign',
                reason: 'campaign_creation'
              },
              outbox: buildExactCampaignLaunchOutbox({
                source: 'dashboard_new_campaign',
                actionType: 'campaign_creation',
                assignedEmails: [emailData.email],
                script: emailData.script,
                askFields,
                nonRequestableFields,
                launchId,
                clientId,
                contactName: emailData.contactName,
                firstName: emailData.firstName,
                isPersonalized: true,
                rowNumber: property.rowIndex ?? null,
                subject: subject,
                property: property.address ? {
                  address: property.address,
                  city: property.city || '',
                  propertyName: property.propertyName || ''
                } : null,
                // Combined send mode (only present when this broker's rows collapse into one email)
                ...(emailData.sendMode ? { sendMode: emailData.sendMode } : {}),
                ...(emailData.combinedSubject ? { combinedSubject: emailData.combinedSubject } : {}),
                createdAt: serverTimestamp()
              })
            };
        })
      });

      await campaignBatch.commit();

      // Step 5: format timestamp and notify parent
      const now = new Date();
      const formattedTimestamp = [
        now.toLocaleDateString('en-US', {
          month: '2-digit',
          day: '2-digit',
          year: '2-digit'
        }),
        now.toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit'
        })
      ].join(' ');

      // Store created client data for completion screen
      const clientResult = {
        id: clientId,
        name,
        assignedEmails: validEmails,
        emailToProperties: campaignData.emailToProperties || emailToProperties,
        emailScripts: campaignData.emailScripts || normalizedScripts,
        criteria: (campaignData.emailScripts || normalizedScripts)[0] || '',
        secondaryCriteria: (campaignData.emailScripts || normalizedScripts)[1] || null,
        emailToContactMap: emailToContactMap,
        columnConfig: columnConfig,
        extractionFields: columnConfig.extractionFields || [],
        excelUrl: sheetUrl,
        sheetId: sheetId,
        lastUpdated: formattedTimestamp,
        status: "live"
      };

      setCreatedClient(clientResult);
      setStep('complete');
    } catch (err) {
      console.error('Failed to create client:', err);

      // Cleanup: delete orphan Firestore doc if sheet creation failed
      if (createdDocRef) {
        try {
          console.log('Cleaning up orphan Firestore document...');
          await deleteDoc(createdDocRef);
          console.log('Orphan document deleted');
        } catch (cleanupErr) {
          console.error('Failed to cleanup orphan document:', cleanupErr);
        }
      }

      alert(`Error: ${err.message || 'Unknown error creating client. Please try again.'}`);
      // Go back to campaign step so user can retry
      setStep('campaign');
    } finally {
      setLoading(false);
      setIsCreatingPhase(false); // Stop cycling phrases
    }
  };

  // Handle going back from mapping step
  const handleMappingBack = () => {
    // Leaving the mapping step entirely discards its draft; only Back from the
    // outreach step (handleCampaignBack) returns to it.
    mappingDraftRef.current = null;
    setStep('upload');
    setParsedData(null);
    setColumnAnalysis(null);
    setScriptMentionedFields([]);
  };

  // Step 3: the advisor confirms who may open the sheet this wizard creates.
  // The list is their profile's, not this campaign's, so the screen opens
  // pre-filled and an empty list is a perfectly good answer — Next is never
  // blocked by it, and a refused address only warns.
  const handleSheetAccessNext = () => {
    setStep('campaign');
  };

  const handleSheetAccessBack = () => {
    setStep('mapping');
  };

  // Handle going back from campaign step
  const handleCampaignBack = () => {
    setStep('sheetAccess');
  };

  // Handle completion screen - save and close
  const handleCompletionDone = useCallback(() => {
    if (createdClient) {
      onSave(createdClient);
    }
    onClose();
  }, [createdClient, onSave, onClose]);

  // Auto-close completion screen after animation
  useEffect(() => {
    if (step === 'complete' && createdClient) {
      const timer = setTimeout(() => {
        handleCompletionDone();
      }, 2500); // 2.5 seconds to see the animation
      return () => clearTimeout(timer);
    }
  }, [step, createdClient, handleCompletionDone]);

  // Build pseudo-client for embedded StartProjectModal
  const buildPseudoClient = () => {
    if (!parsedData) return null;
    return {
      id: null,
      name: parsedData.name,
      emailToProperties: parsedData.emailToProperties,
      assignedEmails: parsedData.validEmails,
      emailToContactMap: parsedData.emailToContactMap,
      emailScripts: parsedData.normalizedScripts,
      criteria: parsedData.normalizedScripts[0] || '',
      secondaryCriteria: parsedData.normalizedScripts[1] || null,
      // Context for blank-primary "Generate" (generateInitialScript): the full
      // property list (even rows without emails) and the columns marked "Ask".
      columnConfig,
      campaignProperties: deriveCampaignProperties({ properties: parsedData.allProperties }),
      askFields: deriveAskFields(columnConfig),
      // Any uploaded example/ideal outreach scripts, used as light few-shot style.
      exampleScripts: parsedData.normalizedScripts || [],
    };
  };

  // Render based on current step
  const renderContent = () => {
    // Hide modal during sheet creation - show only loading overlay
    if (step === 'creating' && isCreatingPhase && loading) {
      return null; // Loading overlay handles the display
    }

    // Completion screen with checkmark animation - auto-closes after 2.5s
    if (step === 'complete' && createdClient) {
      return (
        <div className="modal" ref={modalRef} style={{ textAlign: 'center', padding: '3rem 2rem' }}>
          <div className="completion-container">
            <div className="completion-checkmark">
              <svg className="checkmark-svg" viewBox="0 0 52 52">
                <circle className="checkmark-circle" cx="26" cy="26" r="25" fill="none"/>
                <path className="checkmark-check" fill="none" d="M14.1 27.2l7.1 7.2 16.7-16.8"/>
              </svg>
            </div>
            <h2 className="completion-title">Campaign Started</h2>
            <p className="completion-subtitle">
              Redirecting to dashboard...
            </p>
          </div>
        </div>
      );
    }

    if (step === 'sheetAccess') {
      return (
        <div className="modal" ref={modalRef}>
          <button className="modal-close-button" onClick={handleCancel}>x</button>
          <SheetAccessAccounts
            variant="step"
            title="Who should be able to open this campaign's sheet?"
            addLabel="Add another Google account you own (Gmail or Google Workspace)"
            signInEmail={userData?.email || ''}
          />
          <div className="mapping-actions">
            <button type="button" className="btn-back" onClick={handleSheetAccessBack}>
              <MdArrowBack />
              Back
            </button>
            <button type="button" className="btn-confirm" onClick={handleSheetAccessNext}>
              Next
              <MdArrowForward />
            </button>
          </div>
        </div>
      );
    }

    if (step === 'campaign' && parsedData) {
      const pseudoClient = buildPseudoClient();
      return (
        <div className="modal modal-wide" ref={modalRef}>
          {!loading && (
            <button className="modal-close-button" onClick={onClose}>x</button>
          )}
          <StartProjectModal
            client={pseudoClient}
            embedded={true}
            mailboxConnected={mailboxConnected}
            onClose={handleCampaignBack}
            onConfirm={handleCampaignConfirm}
          />
        </div>
      );
    }

    if (step === 'mapping' && columnAnalysis) {
      return (
        <div className="modal modal-wide" ref={modalRef}>
          {!loading && (
            <button className="modal-close-button" onClick={onClose}>x</button>
          )}
          <ColumnMappingStep
            columns={columnAnalysis.columns || []}
            hasEmailColumn={columnAnalysis.hasEmailColumn !== false}
            onConfirm={handleMappingConfirm}
            onBack={handleMappingBack}
            savedDraft={mappingDraftRef.current}
            onDraftChange={draft => { mappingDraftRef.current = draft; }}
            onCancel={handleCancel}
            scriptMentionedFields={scriptMentionedFields}
            extractedData={parsedData ? {
              allProperties: parsedData.allProperties || [],
              emailToContactMap: parsedData.emailToContactMap || {},
              clientName: parsedData.name || ''
            } : null}
            validationIssues={validationIssues}
            clientName={parsedData?.name || ''}
            suggestedName={parsedData?.suggestedName || ''}
            existingClientNames={existingNames || []}
          />
        </div>
      );
    }

    // Default: upload step - hide modal during upload loading
    if (isUploadPhase && loading) {
      return null; // Loading overlay handles the display
    }

    return (
      <div className="modal" ref={modalRef}>
        <button className="modal-close-button" onClick={handleCancel}>x</button>
        <h2 className="styled-upload-label">Upload Excel from CoStar</h2>
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx, .xls"
          onChange={handleFileUpload}
          className="styled-upload-input"
          title=""
        />
        {validationIssues
          .filter(issue => issue.field === 'file')
          .map((issue, index) => (
            <div
              key={`${issue.field}-${index}`}
              className={`validation-issue validation-issue--${issue.type}`}
              role="alert"
            >
              {issue.message}
            </div>
          ))}
        <div className="modal-actions">
          <button className="cancel-btn" onClick={handleCancel}>Cancel</button>
        </div>
      </div>
    );
  };

      return (
        <div className="modal-backdrop-2 adjust-higher">
          {renderContent()}
          <CampaignMaintenanceDialog
            isOpen={Boolean(campaignMaintenance)}
            message={campaignMaintenance || maintenanceMessage}
            onClose={() => setCampaignMaintenance(null)}
          />
          {loading && (
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
          <p>{loadingMessage}</p>
        </div>
      )}
    </div>
  );
}
